// ============================================================
// Gemeinsame Bausteine — übernommen aus
// /opt/webgis/frontend/src/components/ui/primitives.tsx.
//
// Gegenüber der Vorlage entfernt: der Dial (Drehknopf — ein Quiz hat keine
// Winkelwerte) und die Anbindung an den i18n-Speicher von webgis; die zwei
// Zeichenketten, die der Select braucht, stehen jetzt als deutsche Literale
// darin. Sonst ist die Schnittstelle (Props, Klassennamen) unverändert, damit
// die Stile aus base.css eins zu eins greifen.
// ============================================================
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import type { IconWeight } from "@phosphor-icons/react";

export function Spinner({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <span
      className="spin"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2px solid ${color || "var(--field-border)"}`,
        borderTopColor: color ? "transparent" : "var(--accent-text)",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "subtle" | "danger";
  icon?: string;
  iconWeight?: IconWeight;
  size?: "sm" | "md" | "lg";
}

export function Btn({ variant = "primary", icon, iconWeight, children, className = "", size = "md", style = {}, ...rest }: BtnProps) {
  const sizeStyle: CSSProperties =
    size === "sm" ? { padding: "7px 13px", fontSize: 13 } : size === "lg" ? { padding: "13px 24px", fontSize: 15 } : {};
  return (
    <button className={`btn btn-${variant} ring-focus ${className}`} style={{ ...sizeStyle, ...style }} {...rest}>
      {icon && <Icon name={icon} weight={iconWeight} size={size === "sm" ? 15 : 17} />}
      {children}
    </button>
  );
}

export interface IconBtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  weight?: IconWeight;
  size?: number;
  box?: number;
  active?: boolean;
}

export function IconBtn({ icon, weight, size = 18, box = 38, active, title, className = "", style = {}, ...rest }: IconBtnProps) {
  return (
    <button
      title={title}
      aria-label={title}
      className={`icon-btn ring-focus ${className}`}
      style={{
        width: box,
        height: box,
        background: active ? "var(--accent-soft)" : undefined,
        color: active ? "var(--accent-text)" : undefined,
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} weight={weight} size={size} />
    </button>
  );
}

export function Field({ label, hint, children, className = "" }: { label?: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {label && <label className="lbl">{label}</label>}
      {children}
      {hint && <p style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 6 }}>{hint}</p>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: string;
  mono?: boolean;
  /**
   * Seit React 19 ist `ref` eine gewoehnliche Prop einer Funktionskomponente —
   * forwardRef braucht es dafuer nicht mehr. Sie landet ueber ...rest ohnehin
   * am <input>; hier steht sie nur, damit der Typ das auch zulaesst. Ohne diese
   * Zeile scheitert jeder Aufrufer, der das Feld fokussieren will, an TS2322,
   * obwohl es zur Laufzeit funktioniert haette.
   */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ icon, className = "", mono, style, ...rest }: InputProps) {
  if (icon)
    return (
      <div style={{ position: "relative" }}>
        <Icon
          name={icon}
          size={16}
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-mute)", pointerEvents: "none" }}
        />
        <input className={`inp ${mono ? "mono" : ""} ${className}`} style={{ paddingLeft: 36, ...style }} {...rest} />
      </div>
    );
  return <input className={`inp ${mono ? "mono" : ""} ${className}`} style={style} {...rest} />;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

/** Freitextfeld — dieselbe Feldoptik wie <Input>, nur mehrzeilig. Neu
 *  gegenüber webgis: die Freitextantwort und die Bewertungshinweise für die
 *  KI sind die beiden Felder, um die sich dieses Quiz dreht. */
export function Textarea({ className = "", mono, rows = 4, style, ...rest }: TextareaProps) {
  return (
    <textarea
      rows={rows}
      className={`inp ${mono ? "mono" : ""} ${className}`}
      style={{ resize: "vertical", lineHeight: 1.55, ...style }}
      {...rest}
    />
  );
}

export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> {
  value: string | null | undefined;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  /** schmalerer Auslöser, damit er in einer Kopfzeile mitläuft */
  compact?: boolean;
  /** Filterfeld über der Liste; ab 12 Einträgen von selbst an */
  searchable?: boolean;
}

// Eigenes Auswahlfeld statt <select>: die aufgeklappte Liste eines nativen
// <select> zeichnet das Betriebssystem, sie lässt sich weder runden noch
// einfärben. Drop-in: gleiche Props, onChange feuert weiterhin ein Ereignis
// der Form {target:{value}}.
export function Select({
  value, onChange, options, className = "", placeholder, disabled, style, compact, searchable,
  // Die NAMENSGEBENDEN Attribute wandern auf den Auslöser-Knopf. `SelectProps`
  // erweitert `SelectHTMLAttributes`, der TYP nimmt sie also an — bis zur
  // Zusammenführung fielen sie zur Laufzeit lautlos weg, und ein Auswahlfeld
  // ohne Namen heißt für eine Vorlesehilfe nur „…, Schaltfläche". Bewusst
  // EINZELN aufgezählt und nicht als `...rest` gespreizt: `SelectHTMLAttributes`
  // trägt Dinge wie `multiple`, `size` und `required`, die ein `<button>` nicht
  // kennt.
  id, title, name,
  "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
}: SelectProps) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const current = norm.find((o) => o.value === (value ?? ""));
  const withSearch = searchable ?? norm.length > 12;
  const needle = q.trim().toLowerCase();
  const shown = needle ? norm.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(needle)) : norm;

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = Math.min(264, norm.length * 38 + 12) + (withSearch ? 46 : 0);
    const up = r.bottom + maxH + 8 > window.innerHeight && r.top - maxH - 8 > 0;
    setPos({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: r.width, up });
  }, [norm.length, withSearch]);

  useLayoutEffect(() => {
    if (open) {
      setQ("");
      place();
    }
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // Schließen nur bei Klick daneben oder Escape — Scrollen und
    // Größenänderung RÜCKEN bloß nach, damit die Liste am Feld kleben bleibt
    // (wie ein natives <select>), statt zuzuschnappen.
    const onDown = (e: Event) => {
      if (listRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // die offene Liste verbraucht das Escape VOLLSTÄNDIG — sonst schlösse
        // dasselbe Escape auch ein Modal darüber und würfe dessen Eingaben weg
        e.stopPropagation();
        // ein gefülltes Filterfeld frisst das erste Escape (leeren), das
        // zweite schließt
        setQ((cur) => {
          if (cur) return "";
          setOpen(false);
          return cur;
        });
      }
    };
    const onScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return; // in der Liste selbst scrollen ist in Ordnung
      place();
    };
    const onResize = () => place();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, place]);

  const choose = (v: string) => {
    onChange({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>);
    setOpen(false);
    btnRef.current?.focus();
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        className={`inp ${className}`}
        id={id}
        title={title}
        name={name}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, textAlign: "left", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1, ...(compact ? { padding: "6px 11px", fontSize: 13, borderRadius: 9 } : {}) }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: current ? "var(--text)" : "var(--text-mute)" }}>
          {current?.label ?? placeholder ?? ""}
        </span>
        <Icon name="caret-down" size={14} style={{ color: "var(--text-mute)", flex: "none", transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={listRef}
            className="scroll anim-pop"
            role="listbox"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.up ? undefined : pos.top,
              bottom: pos.up ? window.innerHeight - pos.top : undefined,
              width: pos.width,
              maxHeight: 264,
              overflowY: "auto",
              borderRadius: 12,
              padding: 5,
              zIndex: 1000,
              background: "var(--panel-solid)",
              border: "1px solid var(--glass-border-strong)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {withSearch && (
              <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "1px 1px 6px", background: "var(--panel-solid)" }}>
                <Input
                  icon="magnifying-glass"
                  autoFocus
                  value={q}
                  placeholder="Suchen"
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter auf einem eindeutigen Treffer wählt ihn direkt
                    if (e.key === "Enter" && shown.length === 1) choose(shown[0]!.value);
                  }}
                  style={{ padding: "7px 10px 7px 34px", fontSize: 13, borderRadius: 9 }}
                />
              </div>
            )}
            {withSearch && shown.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--text-mute)" }}>Kein Treffer</div>
            )}
            {shown.map((o) => {
              const on = o.value === (value ?? "");
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => choose(o.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    fontFamily: "inherit",
                    fontWeight: on ? 500 : 400,
                    background: on ? "var(--accent-soft)" : "transparent",
                    color: on ? "var(--accent-text)" : "var(--text-dim)",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) e.currentTarget.style.background = "var(--hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!on) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {on ? <Icon name="check" size={13} style={{ flex: "none" }} /> : <span style={{ width: 13, flex: "none" }} />}
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </button>
              );
            })}
          </div>,
          portalRoot(),
        )}
    </div>
  );
}

export function IOSwitch({ on, onChange, disabled }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div
      className={`io ${on ? "on" : ""}`}
      role="switch"
      aria-checked={!!on}
      onClick={() => !disabled && onChange && onChange(!on)}
      style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
    >
      <div className="knob" />
    </div>
  );
}

export interface SliderProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (v: number) => void;
  showVal?: boolean;
  suffix?: string;
  fmt?: (v: number) => string;
}

export function Slider({ value, min = 0, max = 100, step = 1, onChange, showVal = true, suffix = "", fmt }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <input
        type="range"
        className="rng"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange && onChange(Number(e.target.value))}
        style={{ background: `linear-gradient(90deg, var(--accent) ${pct}%, var(--field-border) ${pct}%)` }}
      />
      {showVal && (
        <span className="font-mono tnum" style={{ fontSize: 13, minWidth: 34, textAlign: "right", color: "var(--text)", fontWeight: 600 }}>
          {fmt ? fmt(value) : value}
          {suffix}
        </span>
      )}
    </div>
  );
}

export interface TabDef {
  id: string;
  label: string;
  icon?: string;
}

export interface TabsProps {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  variant?: "underline" | "pill";
  compact?: boolean;
}

export function Tabs({ tabs, active, onChange, className = "", variant = "underline", compact = false }: TabsProps) {
  if (variant === "pill")
    return (
      <div className={`seg ${className}`}>
        {tabs.map((t) => (
          <button key={t.id} type="button" className={active === t.id ? "active" : ""} onClick={() => onChange(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
    );
  return (
    <div className={className} style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid var(--glass-border)", gap: 2 }}>
      {tabs.map((t) => {
        const on = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              position: "relative",
              padding: compact ? "11px 11px" : "11px 16px",
              fontSize: 13.5,
              fontWeight: 500,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: on ? "var(--text)" : "var(--text-mute)",
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "color .15s",
              whiteSpace: "nowrap",
            }}
          >
            {t.icon && <Icon name={t.icon} size={16} />}
            {t.label}
            {on && (
              <span style={{ position: "absolute", left: 8, right: 8, bottom: -1, height: 2, background: "var(--accent-text)", borderRadius: 2 }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

export type ChipTone = "neutral" | "teal" | "good" | "warn" | "error";

const CHIP_TONES: Record<ChipTone, CSSProperties> = {
  neutral: { background: "var(--field)", color: "var(--text-dim)" },
  teal: { background: "var(--accent-soft)", color: "var(--accent-text)" },
  good: { background: "color-mix(in srgb, var(--good) 14%, transparent)", color: "var(--good)" },
  warn: { background: "color-mix(in srgb, var(--warn) 16%, transparent)", color: "var(--warn)" },
  error: { background: "var(--error-soft)", color: "var(--error)" },
};

export function Chip({
  children,
  tone = "neutral",
  icon,
  className = "",
  style = {},
}: {
  children: ReactNode;
  tone?: ChipTone;
  icon?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span className={`chip ${className}`} style={{ ...CHIP_TONES[tone], ...style }}>
      {icon && <Icon name={icon} size={12} weight="bold" />}
      {children}
    </span>
  );
}

export function Avatar({ initials, photo, size = 36, ring }: { initials: string; photo?: string | null; size?: number; ring?: boolean }) {
  return (
    // Die Klasse traegt keine Gestaltung — sie ist der Griff, an dem die
    // Kopfleiste den Kreis auf dem Telefon kleiner stellen kann (app.css,
    // Schwelle 600 px). Die Groesse selbst bleibt am `size`-Wert.
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flex: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: photo ? `center/cover url(${photo})` : "var(--accent)",
        color: "#fff",
        fontWeight: 500,
        fontSize: size * 0.36,
        boxShadow: ring ? "0 0 0 2px var(--bg), 0 0 0 4px var(--accent-ring)" : undefined,
        // Echtes CSS, also die Variable und nicht der blosse Familienname:
        // "Inter" allein faellt vor dem Laden auf die Browservorgabe zurueck
        // (meist Serife) statt auf die angeglichene Ersatzfamilie — bei
        // Initialen in einem runden Feld faellt genau das auf.
        fontFamily: "var(--schrift-text)",
      }}
    >
      {!photo && initials}
    </div>
  );
}

/**
 * Fehlzustand. `aktion` ist der Schlitz fuer eine Knopfzeile UNTER dem Text.
 *
 * Der Schlitz ist nachgetragen (Integrationsbefund zum Erstflug): §6.7.1 verlangt den
 * Fehlzustand des Imports als EmptyState MIT den Knoepfen „Andere Datei waehlen" und
 * „Verwerfen", und §5.7.5/§5.7.6 haben dasselbe Muster. Ohne Schlitz baut jeder
 * Bildschirm seine Knopfzeile als Geschwister daneben — das geht, sieht aber an jeder
 * Stelle anders aus. Ohne `aktion` ist das Ergebnis Byte fuer Byte das alte.
 */
export function EmptyState({ icon, title, sub, aktion }: {
  icon: string;
  title: string;
  sub?: string;
  aktion?: ReactNode;
}) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-mute)" }}>
      <Icon name={icon} weight="light" size={46} style={{ opacity: 0.5 }} />
      <p style={{ fontWeight: 600, color: "var(--text-dim)", marginTop: 14 }}>{title}</p>
      {sub && <p style={{ fontSize: 13, marginTop: 5 }}>{sub}</p>}
      {aktion && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 18, flexWrap: "wrap" }}>
          {aktion}
        </div>
      )}
    </div>
  );
}

export function portalRoot(): HTMLElement {
  return document.getElementById("root") || document.body;
}
